import KarenOrbButton from "./KarenOrb";
/* UniversalContextBar · Pass C · Feb 2026
 * ===========================================================
 * Shared client entry point for the Universal Operational
 * Language. Thin adapter — no business logic. Every submission
 * goes through the canonical orchestrator at
 *     POST /api/universal_intent/session/{id}/turn
 * so every specialist (booking_request, active_lesson_evidence,
 * expense_recorder, earnings_query, navigate_to) is reachable
 * from the same input surface with identical semantics.
 *
 * Work Mode keeps this canonical entry mounted in voice-only mode:
 * the microphone remains primary while dashboard text chrome stays absent.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Send } from "lucide-react";
import { normalizePresentation, isSafeInternalRoute } from "./universal_intent/normalizePresentation";
import UniversalIntentResponseSheet from "./universal_intent/UniversalIntentResponseSheet";
import { privateMailToolOutput, speakPrivateMail } from "../utils/privateMailSpeech";
import { speakKarenNarration } from "../utils/karenNarration";
import { createParkedConversationBudget, formatConversationBudget, loadParkedConversationContext, parkedConversationNotice, parkedConversationSessionPath } from "../lib/parkedConversation.mjs";
import { applyLessonCommandEnvelope, captureLessonCommandScope, lessonDestinationPending } from "./work_mode/lessonCommandBridge.mjs";
import { offerPart3Command } from "../lib/part3CommandHandoff";

const API = process.env.REACT_APP_BACKEND_URL || "";


function newOperationId() {
  const rnd = (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID().replace(/-/g, "").slice(0, 16)
    : Math.random().toString(36).slice(2, 18);
  return `op-${rnd}`;
}


// Never let internal response-contract labels reach the speaker. Realtime
// normally receives clean specialist narration, but this final boundary also
// protects the user if a provider echoes a field name instead of its value.
export function cleanSpokenReply(message) {
  return String(message || "Done.")
    .replace(/\bauthoritative[\s_-]+answer\b/gi, "")
    .replace(/\s{2,}/g, " ")
    .trim() || "Done.";
}


export function UniversalContextBar({ voiceOnly = false }) {
  const [text, setText] = useState("");
  const [listening, setListening] = useState(false);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [conversationBudget, setConversationBudget] = useState(null);
  const [conversationNotice, setConversationNotice] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [presentation, setPresentation] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const recognitionRef = useRef(null);
  const manualVoiceStopRef = useRef(false);
  const mediaRecorderRef = useRef(null);
  const mediaChunksRef = useRef([]);
  const mediaStreamRef = useRef(null);
  const mediaTimerRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const audioContextRef = useRef(null);
  const realtimePeerRef = useRef(null);
  const realtimeChannelRef = useRef(null);
  const realtimeStreamRef = useRef(null);
  const realtimeAudioRef = useRef(null);
  const realtimeAttemptRef = useRef(null);
  const realtimeUnmuteTimerRef = useRef(null);
  const realtimePlaybackActiveRef = useRef(false);
  const handledRealtimeCallsRef = useRef(new Set());
  const privateMailPlaybackRef = useRef(false);
  const privateMailAbortRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isolatedPart3 = /^\/(?:instructor\/)?work-mode\/?$/i.test(location.pathname)
    && Boolean(new URLSearchParams(location.search).get("part3_session"));
  const isolatedPart3Ref = useRef(isolatedPart3);
  isolatedPart3Ref.current = isolatedPart3;
  const { user } = useAuth();
  const conversationOwnerId = String(user?._id || user?.id || "");
  const conversationOwnerRef = useRef(conversationOwnerId);
  conversationOwnerRef.current = conversationOwnerId;
  const isLearner = user?.role === "learner";
  const handledDebriefHandoffsRef = useRef(new Set());
  const debriefSessionRef = useRef(null);
  const debriefEndPendingRef = useRef(false);
  const debriefFinishTimerRef = useRef(null);
  const debriefRetryRef = useRef(null);
  const [lessonOpeningActive, setLessonOpeningActive] = useState(false);
  const lessonOpeningActiveRef = useRef(false);
  useEffect(() => {
    if (isolatedPart3) {
      stopRealtime();
      recognitionRef.current?.abort?.();
      mediaStreamRef.current?.getTracks?.().forEach(track => track.stop());
      setSheetOpen(false);
      setPresentation(null);
    }
  }, [isolatedPart3]);
  useEffect(() => () => {
    // An account change retires the physical stream, including ordinary
    // voice mode. Inert callbacks alone would leave the old mic connected.
    stopRealtime();
    debriefSessionRef.current = null;
    debriefRetryRef.current = null;
    handledDebriefHandoffsRef.current.clear();
    setConversationNotice("");
    setSessionId(null);
  }, [conversationOwnerId]);
  useEffect(() => {
    const handoff = event => {
      const active = Boolean(event.detail?.active);
      lessonOpeningActiveRef.current = active;
      setLessonOpeningActive(active);
      if (active) {
        stopRealtime();
        recognitionRef.current?.abort?.();
        mediaStreamRef.current?.getTracks?.().forEach(track => track.stop());
      }
    };
    window.addEventListener("ndg:lesson-opening-active", handoff);
    return () => window.removeEventListener("ndg:lesson-opening-active", handoff);
    // Event owns a short exclusive microphone handoff; no request replay.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Work Mode debrief starts a live, parked three-way conversation. Its text
  // outcome is handed to Work Mode only when the instructor ends the session.
  useEffect(() => {
    if (!voiceOnly || typeof window === "undefined") return undefined;
    const handleDebriefHandoff = event => {
      if (isolatedPart3Ref.current) return;
      const lessonId = String(event?.detail?.lessonId || "");
      if (!lessonId || handledDebriefHandoffsRef.current.has(lessonId)) return;
      handledDebriefHandoffsRef.current.add(lessonId);
      stopRealtime();
      debriefRetryRef.current = null;
      if (debriefSessionRef.current?.lessonId !== lessonId) {
        debriefSessionRef.current = { lessonId, transcript: [] };
      }
      startRealtime({ mode: "debrief", lessonId }).catch(() => {
        if (debriefRetryRef.current !== lessonId) return;
        handledDebriefHandoffsRef.current.delete(lessonId);
        setStatus({ kind: "error", message: "Karen couldn't connect to the debrief. Tap the blue circle to retry the debrief, or complete the lesson." });
      });
    };
    const cancelDebrief = () => {
      debriefRetryRef.current = null;
      debriefSessionRef.current = null;
      setConversationBudget(null);
      setConversationNotice("");
      debriefEndPendingRef.current = false;
      if (debriefFinishTimerRef.current) clearTimeout(debriefFinishTimerRef.current);
      debriefFinishTimerRef.current = null;
      stopRealtime();
    };
    window.addEventListener("ndg:karen-debrief-handoff-request", handleDebriefHandoff);
    window.addEventListener("ndg:karen-debrief-cancel", cancelDebrief);
    return () => {
      window.removeEventListener("ndg:karen-debrief-handoff-request", handleDebriefHandoff);
      window.removeEventListener("ndg:karen-debrief-cancel", cancelDebrief);
      cancelDebrief();
    };
  }, [voiceOnly, conversationOwnerId]);

  // Ensure a universal intent session exists as soon as the bar mounts.
  useEffect(() => {
    if (isolatedPart3) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.post(
          `${API}/api/universal_intent/session`,
          {},
          { withCredentials: true },
        );
        if (!cancelled) setSessionId(r?.data?.session_id || null);
      } catch {
        /* stay silent — retry on first submit */
      }
    })();
    return () => { cancelled = true; };
  }, [isolatedPart3]);

  useEffect(() => {
    return () => {
      privateMailAbortRef.current?.abort();
      if (mediaTimerRef.current) clearTimeout(mediaTimerRef.current);
      if (silenceTimerRef.current) clearInterval(silenceTimerRef.current);
      audioContextRef.current?.close?.();
      recognitionRef.current?.stop?.();
      mediaStreamRef.current?.getTracks?.().forEach((track) => track.stop());
      const attempt = realtimeAttemptRef.current;
      realtimeAttemptRef.current = null;
      attempt?.budget?.stop();
      attempt?.controller.abort();
      if (attempt?.timer) clearTimeout(attempt.timer);
      if (realtimeChannelRef.current) {
        realtimeChannelRef.current.onopen = null;
        realtimeChannelRef.current.onmessage = null;
        realtimeChannelRef.current.onclose = null;
        realtimeChannelRef.current.onerror = null;
      }
      if (realtimePeerRef.current) {
        realtimePeerRef.current.ontrack = null;
        realtimePeerRef.current.onconnectionstatechange = null;
      }
      realtimeChannelRef.current?.close?.();
      realtimePeerRef.current?.close?.();
      realtimeStreamRef.current?.getTracks?.().forEach((track) => track.stop());
      realtimeAudioRef.current?.pause?.();
      if (realtimeAudioRef.current) realtimeAudioRef.current.srcObject = null;
      realtimeAudioRef.current?.remove?.();
      if (realtimeUnmuteTimerRef.current) clearTimeout(realtimeUnmuteTimerRef.current);
      realtimePlaybackActiveRef.current = false;
    };
  }, []);

  async function ensureSession() {
    if (sessionId) return sessionId;
    const r = await axios.post(
      `${API}/api/universal_intent/session`,
      {},
      { withCredentials: true },
    );
    const sid = r?.data?.session_id || null;
    setSessionId(sid);
    return sid;
  }

  function pageContext() {
    const bookingDraft =
      typeof window !== "undefined" && window.__ndgBookingDraft
        ? { ...window.__ndgBookingDraft }
        : undefined;
    const workModeContext =
      typeof window !== "undefined" && window.__ndgWorkModeContext
        ? { ...window.__ndgWorkModeContext }
        : undefined;
    const liveLocation =
      typeof window !== "undefined" && window.__ndgPermissionedLiveLocation
        ? { ...window.__ndgPermissionedLiveLocation }
        : undefined;
    return {
      route: location?.pathname || "/",
      surface: (location?.pathname || "").split("/")[1] || "home",
      ...(bookingDraft ? { booking_draft: bookingDraft } : {}),
      ...(workModeContext || {}),
      pending_lesson_destination: lessonDestinationPending(workModeContext?.active_lesson_id),
      ...(liveLocation ? { live_location: liveLocation } : {}),
    };
  }

  function applyBookingDraftUpdate(env) {
    const update = env?.dispatch_result?.draft_update;
    if (!update || typeof window === "undefined") return false;
    window.dispatchEvent(new CustomEvent("ndg:booking-draft-update", { detail: update }));
    return true;
  }

  async function speakKaren(message) {
    if (!message) return false;
    const controller = new AbortController();
    privateMailAbortRef.current?.abort();
    privateMailAbortRef.current = controller;
    privateMailPlaybackRef.current = true;
    try {
      const spoken = await speakKarenNarration(message, { signal: controller.signal });
      if (!spoken && !controller.signal.aborted) {
        setStatus({ kind: "error", message: "Karen's voice is unavailable. The reply is shown on screen; tap Speak to retry." });
      }
      return spoken;
    } finally {
      if (privateMailAbortRef.current === controller) {
        privateMailAbortRef.current = null;
        privateMailPlaybackRef.current = false;
      }
    }
  }

  const dispatch = async (utterance, inputMode = "text") => {
    if (isolatedPart3Ref.current) return;
    const u = (utterance || "").trim();
    if (!u) return;
    const requestOwner = conversationOwnerId;
    const requestLessonScope = captureLessonCommandScope();
    const requestPageContext = pageContext();
    setStatus({ kind: "sending" });
    setBusy(true);
    try {
      const sid = await ensureSession();
      if (isolatedPart3Ref.current || conversationOwnerRef.current !== requestOwner) return;
      if (!sid) {
        setStatus({ kind: "error", message: "Couldn't open a session." });
        return;
      }
      const r = await axios.post(
        `${API}/api/universal_intent/session/${sid}/turn`,
        {
          text: u,
          input_mode: inputMode,
          operation_id: newOperationId(),
          page_context: requestPageContext,
        },
        { withCredentials: true },
      );
      let env = r?.data || {};
      if (isolatedPart3Ref.current || conversationOwnerRef.current !== requestOwner) return;
      if (offerPart3Command(env, conversationOwnerId)) {
        stopRealtime(); setSheetOpen(false); setPresentation(null); setStatus(null);
        navigate("/home");
        window.dispatchEvent(new CustomEvent("ndg:part3-start"));
        return env;
      }
      if (env.specialist_id === "ui_lesson_control") {
        env = await applyLessonCommandEnvelope(env, requestLessonScope) || env;
        const message = env?.dispatch_result?.narration_hint || env.narration;
        setSheetOpen(false);
        setPresentation(null);
        setStatus({ kind: env?.dispatch_result?.control_result?.ok ? "sending" : "clarify", message });
        if (inputMode === "voice") await speakKaren(message);
        return env;
      }
      const speakReply = env?.specialist_id === "ui_icloud_mail" ? speakPrivateMail : speakKaren;
      applyBookingDraftUpdate(env);

      // An explicit navigation command has already supplied user intent.
      // Route immediately when the backend returns a validated internal
      // destination; do not ask the user to confirm "open" a second time.
      if (env.action === "offer_navigation") {
        const route =
          env?.dispatch_result?.navigation?.route ||
          env?.dispatch_result?.navigation_target ||
          null;
        if (isSafeInternalRoute(route)) {
          setStatus(null);
          setSheetOpen(false);
          navigate(route);
          return env;
        }
      }

      // Slice C.1 · normalise envelope → canonical presentation.
      // Renderers never read the raw dispatch_result; they consume
      // this small canonical model instead.
      const p = normalizePresentation(env);

      // Orchestrator-level actions (e.g. permission_refused) still
      // surface as a short inline status — never as a sheet — so
      // access refusals don't feel like a "response."
      const runDayResult = env?.dispatch_result;
      const isRunDayHandoff =
        env?.specialist_id === "ui_run_my_day" &&
        ["ready", "started"].includes(runDayResult?.outcome) &&
        (
          runDayResult?.day_package?.can_launch_work_mode === true ||
          runDayResult?.resume_active_lesson === true
        );
      if (isRunDayHandoff) {
        stopRealtime();
        normalizePresentation(env);
        return env;
      }
      if (env.action === "permission_refused") {
        setStatus({
          kind: "clarify",
          message: env.reason || "You don't have access to that.",
        });
        if (inputMode === "voice") {
          await speakReply(env.reason || p?.narration || "You don't have access to that.");
        }
        return env;
      }

      // Slice C.1 · Navigation is now confirm-tap-required. The bar
      // opens the sheet with NavigateChoice; only user confirmation
      // triggers navigate().
      if (p.kind === "navigate-choice") {
        setPresentation(p);
        setSheetOpen(true);
        setStatus(null);
        if (inputMode === "voice") {
          await speakReply(p?.narration || env?.narration);
        }
        return env;
      }

      // Any other presentation → sheet. Fallback narration renderer
      // handles specialists that have not yet emitted a structured
      // preview field.
      setPresentation(p);
      setSheetOpen(true);
      setStatus(null);
      if (inputMode === "voice") {
        await speakReply(p?.narration || env?.narration);
      }
      return env;
    } catch (e) {
      setStatus({
        kind: "error",
        message:
          e?.response?.data?.detail?.message ||
          e?.response?.data?.detail ||
          "Couldn't reach the operating system. Please try again.",
      });
    } finally {
      setBusy(false);
      setText("");
    }
  };

  const onSubmit = (e) => {
    e?.preventDefault?.();
    if (lessonOpeningActiveRef.current || isolatedPart3Ref.current) return;
    const message = text.trim();
    if (!message) return;

    const channel = realtimeChannelRef.current;
    if (channel?.readyState === "open") {
      // Typed input belongs to the same live conversation as speech. This is
      // especially important for names, postcodes and telephone numbers.
      channel.send(JSON.stringify({
        type: "conversation.item.create",
        item: {
          type: "message",
          role: "user",
          content: [{ type: "input_text", text: message }],
        },
      }));
      channel.send(JSON.stringify({ type: "response.create" }));
      setText("");
      setBusy(true);
      setStatus({ kind: "sending", message: "Understanding…" });
      return;
    }

    dispatch(message);
  };

  function stopRealtime({ abortPrivateSpeech = true, clearStatus = true } = {}) {
    const preservePrivateSpeech = !abortPrivateSpeech && privateMailPlaybackRef.current;
    const attempt = realtimeAttemptRef.current;
    realtimeAttemptRef.current = null;
    attempt?.budget?.stop();
    setConversationBudget(null);
    setConversationNotice("");
    attempt?.controller.abort();
    if (attempt?.timer) clearTimeout(attempt.timer);
    debriefEndPendingRef.current = false;
    if (debriefFinishTimerRef.current) clearTimeout(debriefFinishTimerRef.current);
    debriefFinishTimerRef.current = null;
    if (abortPrivateSpeech) {
      privateMailAbortRef.current?.abort();
      privateMailPlaybackRef.current = false;
    }
    // Retire callbacks before closing. An old connection must never stop a
    // replacement session or send a reply after handing off to Work Mode.
    if (realtimeChannelRef.current) {
      realtimeChannelRef.current.onopen = null;
      realtimeChannelRef.current.onmessage = null;
      realtimeChannelRef.current.onclose = null;
      realtimeChannelRef.current.onerror = null;
      realtimeChannelRef.current.close();
    }
    if (realtimePeerRef.current) {
      realtimePeerRef.current.ontrack = null;
      realtimePeerRef.current.onconnectionstatechange = null;
      realtimePeerRef.current.close();
    }
    realtimeStreamRef.current?.getTracks?.().forEach((track) => track.stop());
    realtimeAudioRef.current?.pause?.();
    if (realtimeAudioRef.current) realtimeAudioRef.current.srcObject = null;
    realtimeAudioRef.current?.remove?.();
    if (realtimeUnmuteTimerRef.current) {
      clearTimeout(realtimeUnmuteTimerRef.current);
      realtimeUnmuteTimerRef.current = null;
    }
    realtimeChannelRef.current = null;
    realtimePeerRef.current = null;
    realtimeStreamRef.current = null;
    realtimeAudioRef.current = null;

    if (preservePrivateSpeech) {
      // Transport/task completion may end the Realtime connection, but it must
      // not pull the plug on Karen while the locally spoken result is active.
      setListening(false);
      setBusy(true);
      setStatus({ kind: "sending", message: "Speaking…" });
      return;
    }

    realtimePlaybackActiveRef.current = false;
    setListening(false);
    setBusy(false);
    if (clearStatus) setStatus(null);
  }

  function finishDebriefConversation() {
    if (!debriefEndPendingRef.current || !debriefSessionRef.current) return;
    debriefEndPendingRef.current = false;
    if (debriefFinishTimerRef.current) clearTimeout(debriefFinishTimerRef.current);
    debriefFinishTimerRef.current = null;
    const session = debriefSessionRef.current;
    debriefSessionRef.current = null;
    debriefRetryRef.current = null;
    window.dispatchEvent(new CustomEvent("ndg:karen-debrief-conversation-finished", {
      detail: { lessonId: session.lessonId, transcript: session.transcript.join(" ").trim() },
    }));
    window.dispatchEvent(new CustomEvent("ndg:karen-debrief-ready", {
      detail: { lessonId: session.lessonId },
    }));
    stopRealtime();
    setStatus({ kind: "sending", message: "Saving lesson outcomes…" });
  }

  function applyRealtimeEnvelope(env) {
    if (!env || isolatedPart3Ref.current) return;
    if (offerPart3Command(env, conversationOwnerId)) {
      stopRealtime(); setSheetOpen(false); setPresentation(null); setStatus(null);
      navigate("/home");
      window.dispatchEvent(new CustomEvent("ndg:part3-start"));
      return;
    }
    if (env.specialist_id === "ui_lesson_control") {
      setSheetOpen(false);
      setPresentation(null);
      setStatus({ kind: env?.dispatch_result?.control_result?.ok ? "sending" : "clarify",
        message: env?.dispatch_result?.narration_hint || env.narration });
      return;
    }
    applyBookingDraftUpdate(env);
    // Spoken navigation has already been confirmed in the live conversation.
    // The navigate specialist currently returns a normal dispatch envelope,
    // while older routes return offer_navigation; both are authoritative here.
    const spokenRoute =
      env?.dispatch_result?.navigation?.route ||
      env?.dispatch_result?.navigation_target ||
      null;
    const isSpokenNavigation =
      env.action === "offer_navigation" ||
      env.specialist_id === "ui_navigate_to" ||
      env?.dispatch_result?.outcome === "navigate";
    if (isSpokenNavigation && isSafeInternalRoute(spokenRoute)) {
      setSheetOpen(false);
      setPresentation(null);
      setStatus(null);
      navigate(spokenRoute);
      return;
    }
    const runDayResult = env?.dispatch_result;
    const isRunDayHandoff =
      env?.specialist_id === "ui_run_my_day" &&
      ["ready", "started"].includes(runDayResult?.outcome) &&
      (
        runDayResult?.day_package?.can_launch_work_mode === true ||
        runDayResult?.resume_active_lesson === true
      );
    if (isRunDayHandoff) {
      // Stop microphone, peer connection and response sheet before navigation.
      stopRealtime();
      setSheetOpen(false);
      setPresentation(null);
      normalizePresentation(env);
      return;
    }
    if (env.action === "permission_refused") {
      setStatus({
        kind: "clarify",
        message: env.reason || env.narration || "You don't have access to that.",
      });
      return;
    }
    const nextPresentation = normalizePresentation(env);
    setPresentation(nextPresentation);
    setSheetOpen(true);
    setStatus(null);
  }

  async function startRealtime({ mode = "normal", lessonId = null } = {}) {
    if (isolatedPart3Ref.current) return;
    stopRealtime();
    const attempt = { controller: new AbortController(), timer: null, mode, lessonId, ownerId: conversationOwnerId };
    realtimeAttemptRef.current = attempt;
    const isCurrent = () => realtimeAttemptRef.current === attempt && !attempt.controller.signal.aborted
      && !lessonOpeningActiveRef.current && !isolatedPart3Ref.current && conversationOwnerRef.current === attempt.ownerId;
    const allowDebriefRetry = () => {
      if (mode !== "debrief") return;
      handledDebriefHandoffsRef.current.delete(String(lessonId || ""));
      debriefRetryRef.current = String(lessonId || "");
    };
    const failConnection = stage => {
      if (!isCurrent()) return;
      const privateSpeechActive = privateMailPlaybackRef.current;
      if (!privateSpeechActive) allowDebriefRetry();
      stopRealtime({ abortPrivateSpeech: !privateSpeechActive, clearStatus: !privateSpeechActive });
      if (privateSpeechActive) return;
      setStatus({ kind: "error", message: stage === "playback"
        ? "Karen connected, but her voice could not play. Tap the blue circle to retry."
        : mode === "debrief"
          ? "Karen's debrief connection failed. Tap the blue circle to retry the debrief, or complete the lesson."
          : "Karen's voice connection failed. Tap the blue circle to reconnect." });
    };
    let sid, stream, conversationContext;
    try {
      if (
        typeof window === "undefined" ||
        typeof window.RTCPeerConnection === "undefined" ||
        !navigator.mediaDevices?.getUserMedia
      ) {
        throw new Error("realtime_not_supported");
      }
      sid = await ensureSession();
      if (!isCurrent()) return;
      if (!sid) throw new Error("session_unavailable");
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 },
      });
      if (!isCurrent()) { stream.getTracks().forEach(track => track.stop()); return; }
      if (mode === "debrief") {
        // Keep the microphone closed until the exact lesson plan is accepted.
        stream.getAudioTracks().forEach(track => { track.enabled = false; });
        conversationContext = await loadParkedConversationContext({ lessonId, mode, signal: attempt.controller.signal, fetcher: fetch });
        if (!isCurrent()) { stream.getTracks().forEach(track => track.stop()); return; }
        setConversationNotice(parkedConversationNotice(conversationContext));
      } else setConversationNotice("");
    } catch (error) {
      stream?.getTracks().forEach(track => track.stop());
      if (!isCurrent()) return;
      allowDebriefRetry();
      stopRealtime();
      throw error;
    }
    if (!isCurrent()) { stream.getTracks().forEach(track => track.stop()); return; }
    if (mode === "debrief") {
      const currentLessonId = String(lessonId || "");
      if (debriefSessionRef.current?.lessonId !== currentLessonId) {
        debriefSessionRef.current = { lessonId: currentLessonId, transcript: [] };
      }
      debriefEndPendingRef.current = false;
    }
    setBusy(true);
    setStatus({ kind: "sending", message: "Connecting…" });

    let peer, audio, channel;
    realtimeStreamRef.current = stream;
    try {
      peer = new window.RTCPeerConnection();
      realtimePeerRef.current = peer;
      audio = document.createElement("audio");
      realtimeAudioRef.current = audio;
      audio.autoplay = true;
      audio.setAttribute("playsinline", "true");
      audio.style.display = "none";
      document.body.appendChild(audio);
      stream.getTracks().forEach((track) => peer.addTrack(track, stream));
      channel = peer.createDataChannel("oai-events");
      realtimeChannelRef.current = channel;
    } catch (error) {
      allowDebriefRetry();
      stopRealtime();
      throw error;
    }
    peer.ontrack = (event) => {
      if (!isCurrent()) return;
      audio.srcObject = event.streams?.[0] || new MediaStream([event.track]);
      audio.play?.().catch(() => failConnection("playback"));
    };
    peer.onconnectionstatechange = () => {
      if (!isCurrent()) return;
      if (["failed", "disconnected", "closed"].includes(peer.connectionState)) {
        const privateSpeechActive = privateMailPlaybackRef.current;
        if (!privateSpeechActive) allowDebriefRetry();
        stopRealtime({
          abortPrivateSpeech: !privateSpeechActive,
          clearStatus: !privateSpeechActive,
        });
        if (!privateSpeechActive) {
          setStatus({ kind: "error", message: mode === "debrief"
            ? "Karen's debrief disconnected. Tap the blue circle to retry the debrief."
            : "Live voice disconnected. Tap the blue circle to reconnect." });
        }
      }
    };

    const canSend = () => isCurrent() && realtimeChannelRef.current === channel && channel.readyState === "open";
    attempt.timer = setTimeout(() => failConnection("timeout"), 30000);
    channel.onerror = () => failConnection("channel");
    channel.onclose = () => failConnection("channel");
    channel.onopen = () => {
      if (!canSend()) return;
      clearTimeout(attempt.timer);
      attempt.timer = null;
      setBusy(false);
      setListening(true);
      setStatus({ kind: "sending", message: "Listening… Speak naturally." });
      if (mode === "debrief") {
        debriefRetryRef.current = null;
        handledDebriefHandoffsRef.current.add(String(lessonId || ""));
        attempt.budget = createParkedConversationBudget({
          ownerId: conversationOwnerId, lessonId, mode,
          onUpdate: value => { if (canSend()) setConversationBudget(value); },
          onPacing: pacing => {
            if (!canSend()) return false;
            channel.send(JSON.stringify(pacing));
            return true;
          },
        });
        attempt.budget.start();
        stream.getAudioTracks().forEach(track => { track.enabled = true; });
        channel.send(JSON.stringify({ type: "response.create", response: {
          output_modalities: ["audio"], tool_choice: "none",
        } }));
      }
    };
    channel.onmessage = async (message) => {
      if (!canSend()) return;
      if (privateMailPlaybackRef.current) return;
      let event;
      try {
        event = JSON.parse(message.data);
      } catch {
        return;
      }
      if (event.type === "response.audio.delta" || event.type === "output_audio_buffer.started") {
        // Half-duplex safety lane: do not let Karen's speaker output or nearby
        // noise become a false interruption. A deliberate typed message still
        // works while she is speaking.
        realtimePlaybackActiveRef.current = true;
        if (realtimeUnmuteTimerRef.current) {
          clearTimeout(realtimeUnmuteTimerRef.current);
          realtimeUnmuteTimerRef.current = null;
        }
        realtimeStreamRef.current?.getAudioTracks?.().forEach((track) => {
          track.enabled = false;
        });
        setListening(false);
        setStatus({ kind: "sending", message: "Speaking…" });
      } else if (event.type === "response.audio.done") {
        // This means the server has finished *sending* audio. Safari may still
        // have several seconds buffered locally, so keep the microphone muted
        // until output_audio_buffer.stopped confirms audible playback ended.
        // The fallback prevents a broken/missing browser event from leaving
        // voice permanently muted.
        if (realtimeUnmuteTimerRef.current) clearTimeout(realtimeUnmuteTimerRef.current);
        realtimeUnmuteTimerRef.current = setTimeout(() => {
          realtimePlaybackActiveRef.current = false;
          realtimeStreamRef.current?.getAudioTracks?.().forEach((track) => {
            track.enabled = true;
          });
          setListening(true);
          setStatus({ kind: "sending", message: "Listening… Speak naturally." });
          realtimeUnmuteTimerRef.current = null;
        }, 20000);
      } else if (event.type === "output_audio_buffer.stopped") {
        if (debriefEndPendingRef.current) { finishDebriefConversation(); return; }
        // WebRTC playback has actually stopped on the device. A short guard
        // absorbs iPhone speaker tail/echo before reopening the microphone.
        realtimePlaybackActiveRef.current = false;
        if (realtimeUnmuteTimerRef.current) clearTimeout(realtimeUnmuteTimerRef.current);
        realtimeUnmuteTimerRef.current = setTimeout(() => {
          realtimeStreamRef.current?.getAudioTracks?.().forEach((track) => {
            track.enabled = true;
          });
          setListening(true);
          setStatus({ kind: "sending", message: "Listening… Speak naturally." });
          realtimeUnmuteTimerRef.current = null;
        }, 650);
      } else if (event.type === "input_audio_buffer.speech_started") {
        setStatus({ kind: "sending", message: "Listening…" });
      } else if (event.type === "input_audio_buffer.speech_stopped") {
        setStatus({ kind: "sending", message: "Understanding…" });
      } else if (event.type === "conversation.item.input_audio_transcription.completed") {
        const heard = String(event.transcript || "").trim();
        if (heard) {
          if (debriefSessionRef.current) debriefSessionRef.current.transcript.push(heard);
          setStatus({ kind: "sending", message: `Heard: “${heard}”` });
        }
      } else if (event.type === "response.function_call_arguments.done" && event.name === "end_parked_debrief") {
        if (!event.call_id || handledRealtimeCallsRef.current.has(event.call_id)) return;
        handledRealtimeCallsRef.current.add(event.call_id);
        let args = {};
        try { args = JSON.parse(event.arguments || "{}"); } catch {}
        if (!["stop_here", "finish_debrief"].includes(args.action)) {
          channel.send(JSON.stringify({ type: "conversation.item.create", item: {
            type: "function_call_output", call_id: event.call_id,
            output: JSON.stringify({ ok: false, message: "Ask whether the instructor wants to stop or finish." }),
          } }));
          channel.send(JSON.stringify({ type: "response.create" }));
          return;
        }
        debriefEndPendingRef.current = true;
        channel.send(JSON.stringify({ type: "conversation.item.create", item: {
          type: "function_call_output", call_id: event.call_id,
          output: JSON.stringify({ ok: true, action: args.action }),
        } }));
        channel.send(JSON.stringify({ type: "response.create", response: {
          instructions: "Give a short, warm sign-off. Do not claim that notes were saved.",
        } }));
        debriefFinishTimerRef.current = setTimeout(() => finishDebriefConversation(), 12000);
      } else if (event.type === "response.function_call_arguments.done" && ["ndg_action", "pupil_setup"].includes(event.name)) {
        if (!event.call_id || handledRealtimeCallsRef.current.has(event.call_id)) return;
        handledRealtimeCallsRef.current.add(event.call_id);
        let args = {};
        try {
          args = JSON.parse(event.arguments || "{}");
        } catch {
          args = {};
        }
        try {
          const isPupilSetup = event.name === "pupil_setup";
          const requestLessonScope = captureLessonCommandScope();
          const response = await axios.post(
            isPupilSetup
              ? `${API}/api/universal_intent/realtime/pupil`
              : `${API}/api/universal_intent/realtime/tool`,
            isPupilSetup
              ? args
              : {
                  ...args,
                  session_id: sid,
                  page_context: pageContext(),
                  operation_id: newOperationId(),
                },
            {
              withCredentials: true,
              // Self-repair boundary: a provider or routing lookup must not
              // leave Karen's function call pending forever.
              timeout: 35000,
            },
          );
          let env = response?.data || {};
          if (!canSend()) return;
          env = await applyLessonCommandEnvelope(env, requestLessonScope) || env;
          if (!canSend()) return;
          applyRealtimeEnvelope(env);
          // A successful lesson handoff deliberately closes this transport.
          // Its action is already confirmed; do not reply on the old channel.
          if (!canSend()) return;
          // Realtime must speak the specialist's actual answer. The
          // envelope's top-level narration is only a generic orchestration
          // acknowledgement and can omit the diary facts the user asked for.
          const specialistNarration = cleanSpokenReply(
            env?.dispatch_result?.presentation?.narration ||
            env?.dispatch_result?.narration_hint ||
            env?.narration ||
            "Done."
          );
          if (env?.specialist_id === "ui_icloud_mail") {
            // Never place headers/body/narration from email in a model session
            // that can call booking, messaging or other action tools.
            channel.send(JSON.stringify({
              type: "conversation.item.create",
              item: { type: "function_call_output", call_id: event.call_id,
                output: privateMailToolOutput() },
            }));
            privateMailPlaybackRef.current = true;
            if (realtimeUnmuteTimerRef.current) clearTimeout(realtimeUnmuteTimerRef.current);
            realtimeUnmuteTimerRef.current = null;
            realtimePlaybackActiveRef.current = true;
            realtimeStreamRef.current?.getAudioTracks?.().forEach((track) => { track.enabled = false; });
            if (realtimeAudioRef.current) realtimeAudioRef.current.muted = true;
            setListening(false);
            setBusy(true);
            const controller = new AbortController();
            privateMailAbortRef.current = controller;
            let spoken = false;
            try {
              spoken = await speakPrivateMail(specialistNarration, { signal: controller.signal });
              if (spoken && !controller.signal.aborted) {
                // Absorb speaker-tail audio before enabling the microphone.
                await new Promise((resolve) => setTimeout(resolve, 650));
              }
            } finally {
              privateMailPlaybackRef.current = false;
              realtimePlaybackActiveRef.current = false;
              privateMailAbortRef.current = null;
              const liveChannel =
                realtimeChannelRef.current === channel &&
                channel.readyState === "open" &&
                !controller.signal.aborted;
              if (liveChannel) {
                if (realtimeAudioRef.current) realtimeAudioRef.current.muted = false;
                realtimeStreamRef.current?.getAudioTracks?.().forEach((track) => { track.enabled = true; });
                setListening(true);
                setBusy(false);
                setStatus({ kind: "sending", message: "Listening… Speak naturally." });
              } else if (!controller.signal.aborted) {
                // The task/transport may have completed while Karen was still
                // talking. Finish the sentence first, then leave a clean way
                // back into conversation instead of cutting her off.
                setListening(false);
                setBusy(false);
                setStatus({
                  kind: spoken ? "sending" : "error",
                  message: spoken
                    ? "Finished. Tap the blue circle when you want to carry on."
                    : "Karen couldn't finish speaking. The full result is still on screen. Tap the blue circle to continue.",
                });
              }
            }
            return;
          }
          channel.send(JSON.stringify({
            type: "conversation.item.create",
            item: {
              type: "function_call_output",
              call_id: event.call_id,
              output: JSON.stringify({
                answer: specialistNarration,
                result: env?.dispatch_result || null,
              }),
            },
          }));
          channel.send(JSON.stringify({
            type: "response.create",
            response: {
              instructions: "Speak only the value in the answer field, exactly once. Never say a field name or describe the tool result. Do not shorten it, add a refusal, restart, or retry the tool call.",
            },
          }));
        } catch (error) {
          if (!canSend()) return;
          const timedOut = error?.code === "ECONNABORTED";
          const detail = timedOut
            ? "That request took too long, so I cleared it. Please ask me again."
            : error?.response?.data?.detail || "NDG could not complete that request.";
          channel.send(JSON.stringify({
            type: "conversation.item.create",
            item: {
              type: "function_call_output",
              call_id: event.call_id,
              output: JSON.stringify({ ok: false, error: detail }),
            },
          }));
          channel.send(JSON.stringify({
            type: "response.create",
            response: {
              instructions: timedOut
                ? "Say the previous request was cleared and ask the user to try again. Do not claim it is still processing."
                : "Explain this failure once in one short sentence. Do not retry or rephrase it.",
            },
          }));
          setStatus({ kind: "error", message: typeof detail === "string" ? detail : "That request failed." });
        }
      } else if (event.type === "response.done") {
        if (event.response?.status === "failed") { failConnection("provider"); return; }
        setBusy(false);
        // Audio sessions normally emit response.audio.done, which owns the
        // delayed microphone handoff. Text-only responses can safely restore
        // listening here.
        if (!realtimePlaybackActiveRef.current && !realtimeUnmuteTimerRef.current) {
          realtimeStreamRef.current?.getAudioTracks?.().forEach((track) => {
            track.enabled = true;
          });
          setListening(true);
          setStatus({ kind: "sending", message: "Listening… Speak naturally." });
        }
      } else if (event.type === "error") {
        failConnection("provider");
      }
    };

    try {
      const offer = await peer.createOffer();
      if (!isCurrent()) return;
      await peer.setLocalDescription(offer);
      if (!isCurrent()) return;
      const sessionPath = mode === "debrief" ? parkedConversationSessionPath(conversationContext) : "/api/universal_intent/realtime/session";
      const response = await fetch(`${API}${sessionPath}`, {
        method: "POST",
        body: offer.sdp,
        headers: { "Content-Type": "application/sdp" },
        credentials: "include",
        signal: attempt.controller.signal,
      });
      if (!response.ok) throw new Error(await response.text());
      const sdp = await response.text();
      if (!isCurrent()) return;
      await peer.setRemoteDescription({
        type: "answer",
        sdp,
      });
    } catch (error) {
      if (!isCurrent()) return;
      allowDebriefRetry();
      stopRealtime();
      throw error;
    }
  }

  const startRecordedFallback = async (existingStream = null) => {
    if (isolatedPart3Ref.current) {
      existingStream?.getTracks?.().forEach(track => track.stop());
      return;
    }
    const recordingOwner = conversationOwnerId;
    if (
      typeof navigator === "undefined" ||
      !navigator.mediaDevices?.getUserMedia ||
      typeof MediaRecorder === "undefined"
    ) {
      setStatus({
        kind: "error",
        message: "Voice recording isn't available on this device.",
      });
      return;
    }

    try {
      const stream = existingStream || await navigator.mediaDevices.getUserMedia({ audio: true });
      if (isolatedPart3Ref.current || conversationOwnerRef.current !== recordingOwner) {
        stream.getTracks?.().forEach(track => track.stop());
        return;
      }
      mediaStreamRef.current = stream;
      // Safari only enables spoken audio after a direct user gesture.
      // The first tap both grants the microphone and unlocks Karen's voice.
      if (!existingStream) {
        setStatus({ kind: "sending", message: "Getting ready…" });
        await speakKaren("I'm listening.", 1800);
      }
      const candidates = [
        "audio/mp4",
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/ogg;codecs=opus",
      ];
      const mimeType = candidates.find((type) => MediaRecorder.isTypeSupported?.(type)) || "";
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      mediaChunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (event.data?.size) mediaChunksRef.current.push(event.data);
      };
      recorder.onerror = () => {
        if (mediaTimerRef.current) {
          clearTimeout(mediaTimerRef.current);
          mediaTimerRef.current = null;
        }
        if (silenceTimerRef.current) {
          clearInterval(silenceTimerRef.current);
          silenceTimerRef.current = null;
        }
        audioContextRef.current?.close?.();
        audioContextRef.current = null;
        mediaRecorderRef.current = null;
        setListening(false);
        setStatus({ kind: "error", message: "Voice recording failed. Tap the blue circle and try again." });
      };
      recorder.onstop = async () => {
        if (mediaTimerRef.current) {
          clearTimeout(mediaTimerRef.current);
          mediaTimerRef.current = null;
        }
        if (silenceTimerRef.current) {
          clearInterval(silenceTimerRef.current);
          silenceTimerRef.current = null;
        }
        audioContextRef.current?.close?.();
        audioContextRef.current = null;
        mediaRecorderRef.current = null;
        setListening(false);

        if (isolatedPart3Ref.current) {
          mediaChunksRef.current = [];
          stream.getTracks?.().forEach(track => track.stop());
          return;
        }

        const recordedType = recorder.mimeType || mimeType || "audio/webm";
        const blob = new Blob(mediaChunksRef.current, { type: recordedType });
        mediaChunksRef.current = [];
        if (!blob.size) {
          setStatus({ kind: "clarify", message: "I didn't hear any words. Tap the blue circle and try again." });
          return;
        }

        setStatus({ kind: "sending", message: "Understanding…" });
        try {
          const form = new FormData();
          const extension = recordedType.includes("mp4")
            ? "m4a"
            : recordedType.includes("ogg")
            ? "ogg"
            : "webm";
          form.append("audio", blob, `command-${Date.now()}.${extension}`);
          const response = await axios.post(
            `${API}/api/voice-command/transcribe`,
            form,
            { withCredentials: true },
          );
          const rawTranscript = response?.data?.transcript?.trim?.() || "";
          const transcriptConfidence = Number(response?.data?.confidence);
          const transcript = rawTranscript.replace(
            /^\s*(?:hey\s+)?karen[\s,.:;!-]*/i,
            "",
          ).trim();
          if (!transcript || (Number.isFinite(transcriptConfidence) && transcriptConfidence < 0.72)) {
            stream.getTracks?.().forEach((track) => track.stop());
            mediaStreamRef.current = null;
            setStatus({
              kind: "clarify",
              message: transcript
                ? `I heard “${transcript}”, but I’m not confident that is right. Please try again.`
                : "I didn't hear any words. Tap the blue circle and try again.",
            });
            return;
          }
          setStatus({ kind: "sending", message: `Heard: “${transcript}”` });
          const env = await dispatch(transcript, "voice");
          const needsFollowUp =
            env?.action === "clarify" ||
            env?.dispatch_result?.outcome === "clarify";
          if (needsFollowUp) {
            setTimeout(() => startRecordedFallback(stream), 700);
          } else {
            stream.getTracks?.().forEach((track) => track.stop());
            mediaStreamRef.current = null;
          }
        } catch (error) {
          stream.getTracks?.().forEach((track) => track.stop());
          mediaStreamRef.current = null;
          const detail = error?.response?.data?.detail;
          setStatus({
            kind: "error",
            message: typeof detail === "string"
              ? detail
              : "I couldn't understand that recording. Tap the blue circle and try again.",
          });
        }
      };

      recorder.start();
      try {
        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        if (AudioContextClass) {
          const audioContext = new AudioContextClass();
          audioContextRef.current = audioContext;
          const analyser = audioContext.createAnalyser();
          analyser.fftSize = 512;
          audioContext.createMediaStreamSource(stream).connect(analyser);
          const levels = new Uint8Array(analyser.fftSize);
          let heardVoice = false;
          let lastVoiceAt = Date.now();
          silenceTimerRef.current = setInterval(() => {
            if (recorder.state !== "recording") return;
            analyser.getByteTimeDomainData(levels);
            let energy = 0;
            for (let index = 0; index < levels.length; index += 1) {
              const sample = (levels[index] - 128) / 128;
              energy += sample * sample;
            }
            const rms = Math.sqrt(energy / levels.length);
            if (rms > 0.028) {
              heardVoice = true;
              lastVoiceAt = Date.now();
            } else if (heardVoice && Date.now() - lastVoiceAt > 1200) {
              recorder.stop();
            }
          }, 120);
        }
      } catch {
        /* 15-second safety timeout remains when Web Audio is unavailable. */
      }
      setListening(true);
      setStatus({ kind: "sending", message: "Listening… Tap again when finished." });
      mediaTimerRef.current = setTimeout(() => {
        if (recorder.state === "recording") recorder.stop();
      }, 15000);
    } catch (error) {
      setListening(false);
      const denied = error?.name === "NotAllowedError" || error?.name === "SecurityError";
      setStatus({
        kind: "error",
        message: denied
          ? "Microphone access is blocked. Allow microphone access for NDG in Safari settings."
          : "Microphone unavailable. Tap the blue circle and try again.",
      });
    }
  };

  const toggleMic = async () => {
    if (lessonOpeningActiveRef.current || isolatedPart3Ref.current) return;
    if (realtimeAttemptRef.current || realtimePeerRef.current) {
      if (realtimeAttemptRef.current?.mode === "debrief") {
        debriefRetryRef.current = String(realtimeAttemptRef.current.lessonId || "");
        handledDebriefHandoffsRef.current.delete(debriefRetryRef.current);
      }
      stopRealtime();
      return;
    }
    try {
      const retryLessonId = debriefRetryRef.current;
      if (retryLessonId) await startRealtime({ mode: "debrief", lessonId: retryLessonId });
      else await startRealtime();
    } catch (error) {
      setListening(false);
      setBusy(false);
      const denied =
        error?.name === "NotAllowedError" ||
        error?.name === "SecurityError";
      setStatus({
        kind: "error",
        message: denied
          ? "Microphone access is blocked. Allow microphone access for NDG in Safari settings."
          : "Live ChatGPT voice couldn't connect. Type below, or tap the blue circle to retry.",
      });
    }
  };

  if (isolatedPart3) return null;

  return (
    <div
      data-testid="universal-context-bar"
      className={`flex flex-col gap-1 w-full ${voiceOnly ? "items-center" : ""}`}
      data-voice-only={voiceOnly ? "true" : "false"}
    >
      <form
        data-testid="universal-context-bar-form"
        onSubmit={onSubmit}
        className={`flex items-center gap-2 w-full ${voiceOnly ? "justify-center" : ""}`}
      >
        <KarenOrbButton
          data-testid="universal-context-bar-mic"
          disabled={lessonOpeningActive}
          onClick={toggleMic}
          active={listening}
          large={voiceOnly}
        />
        <input
          data-testid="universal-context-bar-input"
          disabled={lessonOpeningActive}
          value={text}
          onChange={(e) => setText(e.target.value)}
          inputMode="text"
          autoComplete="off"
          placeholder={voiceOnly
            ? "Type instead…"
            : isLearner
              ? "Ask about learning, homework, readiness, or booking…"
              : "Book a lesson, log an expense, ask about your earnings…"}
          className={`flex-1 min-w-0 rounded-full bg-slate-100 dark:bg-slate-800 px-4 py-2 outline-none focus:ring-2 focus:ring-slate-400 ${voiceOnly ? "max-w-[240px]" : ""}`}
        />
        <button
          type="submit"
          data-testid="universal-context-bar-send"
          disabled={!text.trim()}
          className="inline-flex items-center justify-center rounded-full bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 p-2 disabled:opacity-40"
          aria-label="Send"
        >
          <Send size={18} />
        </button>
      </form>
      {voiceOnly && (conversationBudget || conversationNotice) && <div data-testid="debrief-conversation-budget" aria-live="off" className="w-full max-w-md rounded-xl border border-slate-500 bg-slate-900 px-3 py-2 text-center text-sm text-slate-100">
        <p>{formatConversationBudget(conversationBudget)}</p>
        {conversationBudget?.phase === "recap" && <p className="mt-1">Time to recap what helped and what to practise next.</p>}
        {conversationBudget?.phase === "wrap_up" && <p className="mt-1 text-amber-200">Finish the current answer, then end the debrief when ready.</p>}
        {conversationNotice && <p className="mt-1">{conversationNotice}</p>}
      </div>}
      {status && (
        <div
          data-testid="universal-context-bar-status"
          className={`text-xs px-3 pb-1 ${
            status.kind === "error"
              ? "text-red-600"
              : status.kind === "clarify" || status.kind === "unknown"
              ? "text-amber-600"
              : "text-slate-600 dark:text-slate-300"
          }`}
        >
          {status.kind === "sending" ? (status.message || "Sending…") : status.message}
        </div>
      )}
      <UniversalIntentResponseSheet
        open={sheetOpen}
        presentation={presentation}
        listening={listening}
        onDismiss={() => setSheetOpen(false)}
        onConfirmNavigate={(route) => {
          // Slice C.3 · authoritative navigation boundary. Every
          // route that reaches this parent is re-validated before
          // dispatch — regardless of which renderer proposed it.
          // Invalid targets are silently dropped; the sheet stays
          // closed but no navigation occurs.
          setSheetOpen(false);
          if (isSafeInternalRoute(route)) {
            navigate(route);
          }
        }}
      />
    </div>
  );
}

export default UniversalContextBar;



